import { Avatar, Tooltip, Typography } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SMediaLinkedinIcon from '../../../assets/icons/ln.svg?react';
import SMediaTwitterIcon from '../../../assets/icons/tw.svg?react';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';
import { ISourcingPerson } from '../data/sourcingProfileMockData';
import { panelSurfaceCss } from './panelStyles';

const panelCss = css`
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const titleCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
`;

const peopleListCss = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const personRowCss = css`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;

const personLeftCss = css`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  gap: 12px;
  align-items: flex-start;
`;

const personInfoCss = css`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  min-width: 0;
`;

const nameCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.15px;
  color: ${colors.text.main};
  white-space: nowrap;
`;

const roleCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.main};
`;

const secondaryCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const educationRowCss = css`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const socialRowCss = css`
  display: flex;
  gap: 4px;
  align-items: center;
  margin-top: 2px;
`;

const socialIconLinkCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100px;
  overflow: hidden;
  flex-shrink: 0;
  text-decoration: none;
`;

const personRightCss = css`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
`;

const priorRoleLabelCss = css`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.text.secondary};
`;

const historyTableCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const historyRowCss = css`
  display: flex;
  gap: 20px;
  height: 20px;
  align-items: flex-start;
`;

const companyCellCss = css`
  display: flex;
  gap: 4px;
  align-items: flex-start;
  width: 100px;
  flex-shrink: 0;
`;

const companyNameCss = css`
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.primary[60]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 0 0;
  min-width: 0;
`;

const historyRoleCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.text.main};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 0 0;
  min-width: 0;
`;

const companyLogoWrapperCss = css`
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
  background: white;
`;

const letterAvatarCss = css`
  width: 20px;
  height: 20px;
  border-radius: 100px;
  border: 1px solid #d5d7eb;
  background: ${colors.primary[10]};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  letter-spacing: -0.6px;
  color: ${colors.text.main};
  overflow: hidden;
`;

interface KeyPeoplePanelProps {
  readonly people: readonly ISourcingPerson[];
  readonly title: string;
}

export function KeyPeoplePanel({ people, title }: KeyPeoplePanelProps) {
  if (people.length === 0) {
    return null;
  }
  return (
    <div css={[panelSurfaceCss, panelCss]}>
      <Typography css={titleCss}>{title}</Typography>
      <div css={peopleListCss}>
        {people.map((person) => (
          <PersonRow key={person.id} person={person} />
        ))}
      </div>
    </div>
  );
}

function personInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return (parts[0]?.[0] ?? '').toUpperCase();
  }
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

function PersonRow({ person }: { readonly person: ISourcingPerson }) {
  const roleText = person.tenure ? `${person.title} (${person.tenure})` : person.title;

  return (
    <div css={personRowCss}>
      <div css={personLeftCss}>
        <Avatar
          src={person.avatarUrl ?? undefined}
          alt={person.name}
          sx={{
            width: 80,
            height: 80,
            fontSize: 24,
            flexShrink: 0,
            border: `1px solid ${colors.border.card}`,
            bgcolor: colors.primary[10],
            color: colors.text.main,
          }}
        >
          {personInitials(person.name)}
        </Avatar>

        <div css={personInfoCss}>
          <Typography css={nameCss}>{person.name}</Typography>
          <Typography css={roleCss}>{roleText}</Typography>
          {person.headline && <Typography css={secondaryCss}>{person.headline}</Typography>}
          {person.education && (
            <div css={educationRowCss}>
              <ArticleOutlinedIcon sx={{ fontSize: 16, color: colors.text.secondary, flexShrink: 0 }} />
              <Typography css={secondaryCss}>{person.education}</Typography>
            </div>
          )}
          <div css={socialRowCss}>
            {person.linkedinUrl && (
              <SocialIconLink href={person.linkedinUrl} label='LinkedIn'>
                <SMediaLinkedinIcon width={20} height={20} />
              </SocialIconLink>
            )}
            {person.twitterUrl && (
              <SocialIconLink href={person.twitterUrl} label='Twitter / X'>
                <SMediaTwitterIcon width={20} height={20} />
              </SocialIconLink>
            )}
          </div>
        </div>
      </div>

      {person.recentWorkHistory.length > 0 && (
        <div css={personRightCss}>
          <Typography css={priorRoleLabelCss}>Prior Role</Typography>
          <div css={historyTableCss}>
            {person.recentWorkHistory.map((item) => (
              <div key={`${item.company}-${item.title}`} css={historyRowCss}>
                <div css={companyCellCss}>
                  <CompanyLogo company={item.company} logoUrl={item.logoUrl} />
                  <span css={companyNameCss}>{item.company}</span>
                </div>
                <span css={historyRoleCss}>
                  {item.title} ({item.duration})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyLogo({ company, logoUrl }: { readonly company: string; readonly logoUrl: string | null }) {
  if (logoUrl) {
    return (
      <div css={companyLogoWrapperCss}>
        <img src={logoUrl} alt={company} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
    );
  }

  const initials = company
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return <div css={letterAvatarCss}>{initials}</div>;
}

function SocialIconLink({
  href,
  label,
  children,
}: {
  readonly href: string;
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <Tooltip title={label}>
      <a css={socialIconLinkCss} href={href} target='_blank' rel='noopener noreferrer' aria-label={label}>
        {children}
      </a>
    </Tooltip>
  );
}
